import {
  Flex, Button, Text
} from '@huspy/briks-web';
import useClientOpportunity from '@modules/opportunities/entities/opportunity/query/useClientOpportunity';
import { useGetDistinctBankProducts } from '@modules/banks/hooks/queries/useGetDistinctBankProducts';
import { createFormContext, zodResolver } from '@mantine/form';
import briksTheme from '@shared/briks-theme';
import { Skeleton } from '@mantine/core';
import { BaseRate, DistinctBankProduct } from '@modules/banks/api/types';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { validators } from '@huspy/forge/shared';
import { IS_SPAIN_ENV } from '@modules/core/utils';
import { CaseCreationSelectBankProductsRoute } from '@modules/opportunities/presentation/routes';
import { useGetBaseRates } from '@modules/banks/hooks/queries/useGetBaseRates';
import React, { useContext, useMemo } from 'react';
import useSetOpportunityBankRates from '@modules/opportunities/entities/bankApplication/mutation/useSetOpportunityBankRates';
import { CaseCreationContext } from '@modules/opportunities/presentation/pages/CaseCreation/index';
import queryClient from '@app/queryClient';
import { BankApplication } from '@modules/opportunities/entities/bankApplication/bankApplication.entity';
import { BankSelectionCard } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/BankProductsSelection/components/BankSelectionCard';
import {
  mapBankApplicationToBankProducts,
  mapBankProductsToBankRatesApiRequest,
} from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/BankProductsSelection//mappers';
import { BANK_APPLICATION_STATUS } from '@modules/opportunities/entities/bankApplication/const';
import { OpportunityBankProducts } from '@modules/opportunities/presentation/pages/CaseCreation/views/BankSubmission/BankProductsSelection/types';

const { textFieldValidator, numberFieldValidator } = validators;

export const [
  BankProductsSelectionFormProvider,
  useBankProductsSelectionFormContext,
  useBankProductsSelectionForm
] = createFormContext<OpportunityBankProducts>();

const manualSchema = z.object({
  bank_external_id: textFieldValidator,
  is_manual: z.literal(true),
  is_primary: z.boolean(),
  rate_type: textFieldValidator,
  bank_rate: numberFieldValidator,
  term: z.union([textFieldValidator, numberFieldValidator]),
  eibor_type: textFieldValidator.optional().nullable(),
});

const externalRateSchema = z.object({
  bank_external_id: textFieldValidator,
  is_manual: z.literal(false),
  is_primary: z.boolean(),
  rate_type: textFieldValidator,
  bank_rate: numberFieldValidator,
  rate_external_id: textFieldValidator,
  eibor_type: textFieldValidator.optional().nullable(),
});

const schema = z.object({
  banks: z.array(
    z.discriminatedUnion('is_manual', [manualSchema, externalRateSchema])
  ),
});

type Props = {
  bankProducts: DistinctBankProduct[];
  bankApplications: BankApplication[];
  baseVariableRates: BaseRate[];
  children: React.ReactNode;
};

const BankProductsSelection = ({
  bankApplications,
  bankProducts,
  baseVariableRates,
  children,
}: Props) => {
  const router = CaseCreationSelectBankProductsRoute;
  const { opportunityId } = router.useParams();
  const { goToNextState, unlockNextStep } = useContext(CaseCreationContext);

  const form = useBankProductsSelectionFormContext();

  const { mutateAsync: setBankRates, isPending: isSetBankRatesPending } = useSetOpportunityBankRates(opportunityId, () => {
    unlockNextStep();
    goToNextState();
  });

  const isLoading = isSetBankRatesPending;

  const onSubmit = async () => {
    if (form.isDirty()) {
      await setBankRates({
        opportunityId,
        body: mapBankProductsToBankRatesApiRequest(form.values),
      });
    } else {
      goToNextState();
    }
  };

  const filteredBankProducts = bankProducts.filter(
    (bankProduct) =>
      !!bankApplications
        .map((bank) => bank.bank_details.external_id)
        ?.includes(bankProduct.external_id)
  );

  const isOneSubmitted = bankApplications.some(
    (bankApplication) => bankApplication.status !== BANK_APPLICATION_STATUS.draft
  );

  return (
    <form onSubmit={ form.onSubmit(onSubmit) }>
      <Flex direction='column' gap={ 3 }>
        {filteredBankProducts.map((bankProduct) => {
          const bankApplication = bankApplications.find(
            (item) => item.bank_details.external_id === bankProduct.external_id
          );
          return (
            <BankSelectionCard
              key={ bankProduct.external_id }
              baseVariableRates={ baseVariableRates }
              disabled={ isLoading }
              bankSubmissionChannelType={ bankApplication?.bank_submission_channel_type! }
              canTogglePrimaryBankApplication={ !isOneSubmitted }
              isPrimary={ Boolean(bankApplication?.is_primary) }
              isSubmitted={ bankApplication?.status !== BANK_APPLICATION_STATUS.draft }
              { ...bankProduct }
            />
          );
        })}
      </Flex>
      {children}
    </form>
  );
};

const BankProductsSelectionPage = () => {
  const router = CaseCreationSelectBankProductsRoute;
  const { opportunityId } = router.useParams();

  const { data: opportunityData } = useClientOpportunity(opportunityId);
  const { data: bankProducts = [], isLoading: isBanksLoading } = useGetDistinctBankProducts();
  const opportunity = opportunityData?.opportunity;
  const { goToNextState } = useContext(CaseCreationContext);
  const { t } = useTranslation();
  const isMutating = !!queryClient.isMutating({ mutationKey: ['useSetOpportunityBankRates'] });

  const { data: baseVariableRates, isLoading: isBaseRatesLoading } = useGetBaseRates();

  const selectedInitial = useMemo(
    () =>
      mapBankApplicationToBankProducts(opportunity?.bank_applications || []),
    [opportunity?.bank_applications]
  );

  const form = useBankProductsSelectionForm({
    initialValues: { banks: selectedInitial.banks ?? [] },
    validate: zodResolver(schema),
    validateInputOnBlur: true,
    validateInputOnChange: true,
    initialDirty: {},
  });

  const isLoading = isBanksLoading || isBaseRatesLoading;

  return (
    <Flex direction='column' gap={ 8 }>
      <Flex direction='column' gap={ 6 }>
        <Flex>
          <Text size='2xl' color='text-primary'>
            {t('opportunity.caseSubmission.bankProductsSelection.title')}
          </Text>
        </Flex>

        {!isLoading ? (
          <BankProductsSelectionFormProvider form={ form }>
            <BankProductsSelection
              bankProducts={ bankProducts }
              bankApplications={ opportunity?.bank_applications || [] }
              baseVariableRates={ baseVariableRates || [] }
            >
              <Flex justify='space-between' mt='6'>
                <Flex gap={ 4 }>
                  {IS_SPAIN_ENV ? (
                    <Button
                      disabled={ isLoading }
                      loading={ isLoading }
                      onClick={ goToNextState }
                    >
                      {t(
                        'opportunity.caseSubmission.bankProductsSelection.skip'
                      )}
                    </Button>
                  ) : null}
                </Flex>

                <Button
                  disabled={ isLoading || isMutating }
                  loading={ isLoading || isMutating }
                  variant={ isLoading || isMutating ? 'outline' : 'primary' }
                  type='submit'
                  data-test='proceed-btn'
                >
                  {t(
                    'opportunity.caseSubmission.bankProductsSelection.proceed'
                  )}
                </Button>
              </Flex>
            </BankProductsSelection>
          </BankProductsSelectionFormProvider>
        ) : (
          <Flex gap={ 3 } direction='column'>
            <Skeleton radius={ briksTheme.radius['radius-12'] } h={ 90 } w='100%' />
            <Skeleton radius={ briksTheme.radius['radius-12'] } h={ 90 } w='100%' />
            <Skeleton radius={ briksTheme.radius['radius-12'] } h={ 90 } w='100%' />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};

export default BankProductsSelectionPage;
